import React from "react";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { ArrowRight, Users, Building2 } from "lucide-react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

function DualCTASection() {
  const theme = useTheme();
  const i18nLocale = useSelector((state) => state.i18nLocale);

  return (
    <Box
      component="section"
      sx={{
        py: { xs: "3rem", md: "4rem" },
        px: { xs: "1rem", sm: "1.5rem", lg: "2rem" },
        bgcolor: theme.palette.common.white,
      }}
    >
      <Box
        sx={{
          maxWidth: "72rem",
          mx: "auto",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: "1rem",
        }}
      >
        <Box
          component={motion.div}
          onClick={() => { window.location.href = `/${i18nLocale}/candidates/sign_up`; }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "1rem",
            p: { xs: "1.75rem", md: "2rem" },
            cursor: "pointer",
            background: "linear-gradient(135deg, hsl(153 68% 25%), hsl(178 93% 10%))",
            transition: "all 0.3s ease",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "0 0px 1px 1px rgba(0, 0, 0, 0.35)",
              ".cta-arrow": {
                transform: "translateX(4px)",
              },
            },
            "& .cta-arrow": {
              transition: "transform 0.3s ease",
            },
          }}
        >
          <Box sx={{ position: "absolute", top: "1.5rem", right: "1.5rem", color: alpha(theme.palette.common.white, 0.05) }}>
            <Users size={80} />
          </Box>

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                px: "0.625rem",
                py: "0.25rem",
                mb: "0.75rem",
                borderRadius: "9999px",
                bgcolor: alpha(theme.palette.common.white, 0.1),
                color: alpha(theme.palette.common.white, 0.8),
                fontSize: "0.75rem",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="dual_cta.candidate.badge" />
            </Box>
            <Box
              component="h3"
              sx={{
                m: 0,
                mb: "0.5rem",
                color: "hsl(0 0% 100%)",
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                fontFamily: "Segoe UI Black, Work Sans, sans-serif",
              }}
            >
              <FormattedMessage id="dual_cta.candidate.title" />
            </Box>
            <Box
              component="p"
              sx={{
                m: 0,
                mb: "1.25rem",
                maxWidth: "20rem",
                color: alpha(theme.palette.common.white, 0.5),
                fontSize: "0.875rem",
                lineHeight: 1.5,
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="dual_cta.candidate.description" />
            </Box>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                color: theme.palette.common.white,
                fontSize: "0.875rem",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="dual_cta.candidate.cta" /> <ArrowRight size={14} className="cta-arrow" />
            </Box>
          </Box>
        </Box>

        <Box
          component={motion.div}
          onClick={() => { window.location.href = `/${i18nLocale}/recruiters`; }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.08 }}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "1rem",
            p: { xs: "1.75rem", md: "2rem" },
            cursor: "pointer",
            bgcolor: "hsl(178 93% 10%)",
            transition: "all 0.3s ease",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "0 0px 1px 1px rgba(0, 0, 0, 0.35)",
              ".cta-arrow": {
                transform: "translateX(4px)",
              },
            },
            "& .cta-arrow": {
              transition: "transform 0.3s ease",
            },
          }}
        >
          <Box sx={{ position: "absolute", top: "1.5rem", right: "1.5rem", color: alpha(theme.palette.common.white, 0.05) }}>
            <Building2 size={80} />
          </Box>

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                px: "0.625rem",
                py: "0.25rem",
                mb: "0.75rem",
                borderRadius: "9999px",
                bgcolor: alpha(theme.palette.common.white, 0.1),
                color: alpha(theme.palette.common.white, 0.8),
                fontSize: "0.75rem",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="dual_cta.recruiter.badge" />
            </Box>
            <Box
              component="h3"
              sx={{
                m: 0,
                mb: "0.5rem",
                color: theme.palette.common.white,
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                fontFamily: "Segoe UI Black, Work Sans, sans-serif",
              }}
            >
              <FormattedMessage id="dual_cta.recruiter.title" />
            </Box>
            <Box
              component="p"
              sx={{
                m: 0,
                mb: "1.25rem",
                maxWidth: "20rem",
                color: alpha(theme.palette.common.white, 0.5),
                fontSize: "0.875rem",
                lineHeight: 1.5,
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="dual_cta.recruiter.description" />
            </Box>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                color: theme.palette.common.white,
                fontSize: "0.875rem",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="dual_cta.recruiter.cta" /> <ArrowRight size={14} className="cta-arrow" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DualCTASection;